/**
 * The top-down table.
 *
 * ============================== WHAT THIS IS ===================================
 * `mockups/td03-topdown-2026-09-05.html` as a component. Not a re-imagining of it:
 * the ellipse, the clamps, the padding factors and the rotation are that mock's, and
 * `table/topdown.css` names the line each one came from.
 *
 * It renders from a `TableState` and nothing else — the same constraint `GameTable`
 * has, and for the same reason: this component cannot reach into the engine, so it
 * cannot tell whether the snapshot came from the game running in this tab or arrived
 * over a socket. Everything else it takes is presentation the snapshot has no opinion
 * about: which plate image to draw, what is in the wallet, what a seat's face is
 * doing, how far round a timer ring has gone.
 *
 * ## What is NOT a prop
 *
 * Orientation. The spec is explicit that one markup serves every size and that the
 * hero zone and action bar become overlays above a 1.3 aspect and stack below it —
 * and that is a container query in `topdown.css`, never a measurement taken in
 * JavaScript. The only concession is that seats sit on a slightly different ellipse in
 * each orientation, so this emits BOTH sets of coordinates as custom properties and
 * lets CSS pick. Nothing here listens to a resize.
 *
 * ## What is not here yet
 *
 * Time. The timer ring is a number this component draws; the clock that moves it is
 * Phase 3's Director. The taunt bubble is a slot; the trigger table is Phase 6. The
 * wallet is a prop; the real balance is Phase 4. Each of those is deliberately a
 * value coming in rather than a behaviour living here, so the seat's geometry is
 * settled and reviewed before anything starts driving it.
 * ===============================================================================
 */
import type { ReactNode } from "react";
import type { TableState } from "@engine/table-state";
import type { DeckState } from "./useScene";
import type { Payoff } from "./payoff";
import type { Expression } from "../art/roster";
import {
  seatPositions,
  TOPDOWN_RING_LANDSCAPE, TOPDOWN_RING_PORTRAIT,
} from "../lib/table-game";
import { FlightLayer, BubbleLayer, type FlightBus } from "./flight";
import { durationsFor, type Durations } from "./pace";
import type { TauntItem } from "./lines";
import {
  Portrait, TimerRing, SeatPeek, SeatTag, SeatBet, DealerButton, DeckSpot, Board, PotWrap,
  HeroHand, BoardStatus, TalkStatus, TopBar, useBubbles, useCollected, motionVars, seatClass, seatIsEmpty,
  type SeatTimer, type WalletView,
} from "./parts";
import "./topdown.css";

/*
 * Phase 7: every child this table draws lives in `parts.tsx` now, shared with the
 * saloon view (`SaloonTable.tsx`). What is left in this file is the one thing that is
 * top-down's own — WHERE each piece goes: the seat ring in percentages, both
 * orientations emitted, CSS choosing. The markup is unchanged to the attribute.
 */
export type { SeatTimer, WalletView };



export interface TopDownTableProps {
  state: TableState | null;
  /** Seat and label of the last action, for the context line. Same shape `GameTable` takes. */
  lastAction?: { seat: number; label: string } | null;
  /** The plate image and its width ÷ height. Both props so the tier can change later. */
  plate?: string;
  plateAspect?: number;
  /** The line under the wordmark: "6-max · 10bb · Bubble". */
  context?: string;
  wallet?: WalletView | null;
  /**
   * Whose top bar this is.
   *
   * `"own"` — the harness, and anything that renders the table on its own — keeps the
   * table's bar: the wordmark, the context line, the wallet and the ⋯. `"room"` says
   * the table is inside a room that already has a header, and the bar is not drawn at
   * all. Phase 2b's review found the two stacked: the same information twice, two gears
   * opening the same sheet, and about fifty pixels of chrome a phone cannot spare. The
   * room header takes the ⋯'s job (its gear) and the wallet's slot.
   */
  chrome?: "room" | "own";
  /** Per-seat timer ring, keyed by seat index. Phase 3 drives it. */
  timers?: Record<number, SeatTimer>;
  /** Per-seat expression. Phase 6 drives it; the roster falls back to neutral. */
  expressions?: Record<number, Expression>;
  /**
   * Per-seat taunt. Phase 6 drives it.
   *
   * A plain string is a LINE — that is what the Phase 2 harness has always passed and
   * it still works, and it is what makes the fixtures a real test of the placer rather
   * than of a second code path. A `TauntItem` is what `useVoice` hands over: a
   * sticker, an emoji or a line, with the id the cosmetic table knows it by.
   */
  taunts?: Record<number, string | TauntItem>;
  /**
   * Phase 6: cards a seat has TURNED OVER without being made to.
   *
   * A bot showing an uncontested win, or the hero pressing Show. The presented state
   * has `hole: null` for these seats — correctly, because the engine never decided the
   * viewer was entitled to them — so the choice to show is carried beside the state
   * rather than smuggled into it. `Game.liveHoles` is the source and it refuses while
   * a hand is live, which is what makes this safe to hand a renderer.
   */
  shown?: Record<number, readonly number[]>;
  /**
   * Phase 6, box 0(3): seat index -> roster slug, for this sitting only.
   *
   * The engine deals the cast from the session seed and `app/engine/` is closed, so a
   * seat that happens to wear the hero's own persona cannot be swapped for a different
   * PERSON. This swaps their FACE instead — see `art/casting.ts`. Omitted, every seat
   * resolves its portrait from its name exactly as it did before.
   */
  faces?: Record<number, string>;
  /**
   * The action bar, as a child.
   *
   * `ActionBar` is a shared component that needs the engine's legal actions and a
   * callback, neither of which belongs in a scene that renders from a snapshot. So the
   * route builds it and passes it in, and this decides only WHERE it goes — which in
   * landscape is an overlay in the bottom-right corner and in portrait is a band under
   * the hero. One action bar, two placements, no second component.
   */
  actions?: ReactNode;
  /**
   * Phase 3: cards that have ARRIVED at each seat this hand, keyed by seat index.
   *
   * Omitted, every live seat shows its two backs at once — the harness fixtures. Given,
   * a seat shows as many backs as have landed, so the deal can be watched.
   */
  dealt?: Record<number, number>;
  /** Phase 3: the Director's steps, for the flight layer. Omitted, nothing flies. */
  flights?: FlightBus;
  /** Phase 3: the pace, for the flips, hops and counting numbers the scene animates itself. */
  motion?: Durations;
  /**
   * Phase 3: what the ⋯ button opens, as a popover of its own.
   *
   * The harness still uses it. THE ROUTE NO LONGER DOES: Phase 2b gave the player one
   * Settings sheet, reached from the gear in the room header and from this button, and
   * two places to set the pace is one place too many. Prefer `onSettings`.
   */
  settings?: ReactNode;
  /**
   * Phase 2b: the ⋯ hands the tap upwards instead of opening anything itself.
   *
   * Given, the button is a way into the room's Settings sheet. Given neither this nor
   * `settings`, it is decorative, as it was.
   */
  onSettings?: () => void;
  /**
   * Phase 2b: the HERO'S portrait, resolved by the client.
   *
   * The engine names the viewer "You" and carries a null `avatarId` on every seat, on
   * purpose — a portrait must never be able to leak an archetype. The player's own face
   * is therefore a client fact: they pick a roster entry, `settings/prefs.ts` keeps it
   * on the device, and the URL arrives here. Null draws the silhouette, which is the
   * truth until they have chosen.
   */
  avatarUrl?: string | null;
  /** Phase 3b: the deck at the dealer's spot — five backs, riffling, or a two-card stub. */
  deck?: DeckState;
  /** Phase 3b: who won and who showed and lost, for the ring pulse, the lift and the dim. */
  payoff?: Payoff | null;
  /** Phase 3b: the pot's chips are in flight to the winner; the pile under the pill hides. */
  potFlying?: boolean;
  /** Phase 3b: bets are flying in; the pile keeps its old size until they have landed. */
  potFrozen?: boolean;
}

/** Normal pace, for a page that renders the table without choosing one (the harness). */
const NORMAL = durationsFor("normal");

/** The plate `sync-art.mjs` ships today, and its measured aspect: 1100 × 792. */
const DEFAULT_PLATE = "/art/table/poker-topdown-1100.webp";
const DEFAULT_PLATE_ASPECT = 1100 / 792;


/**
 * How far the dealer button sits off its seat's bet chip, as a fraction of the box.
 *
 * The mock's number, and its reasoning: in the mock the hero is always on the button,
 * so the disc is placed at the hero's bet plus 0.10 of the box width. Here the button
 * moves, so the offset is applied along the perpendicular of the seat's own bearing —
 * for a seat at six o'clock that IS +x, so the hero's disc lands exactly where the
 * mock put it and every other seat's swings round with the seat.
 */
const BUTTON_OFFSET = { landscape: 0.10, portrait: 0.13 };
/** The mock also lifts the disc slightly clear of the chips. */
const BUTTON_LIFT = 0.02;

/**
 * The disc's position, in the same percentage coordinates as everything else.
 *
 * `bet` is where the chips are and `seat` is where the player is; the bearing between
 * the centre and the seat gives the direction to swing the disc round in.
 */
function buttonPoint(
  seat: { x: number; y: number; betX: number; betY: number },
  cx: number, cy: number, offset: number,
): { x: number; y: number } {
  const dx = seat.x - cx, dy = seat.y - cy;
  const len = Math.hypot(dx, dy) || 1;
  // Perpendicular, chosen so a seat at the bottom pushes the disc to the RIGHT.
  const px = dy / len, py = -dx / len;
  return { x: seat.betX + px * offset * 100, y: seat.betY + py * offset * 100 - BUTTON_LIFT * 100 };
}

/**
 * The four placement properties a felt element needs, as a style object.
 *
 * Two points go in, one per orientation, and CSS picks — see `topdown.css`. `at` reads
 * the seat's own point and `atBet` the inner ring; they are two functions rather than
 * one with a flag because passing a whole `SeatBox` to a single reader is exactly how
 * the first render put every bet chip on top of its own avatar.
 */
const at = (l: { x: number; y: number }, p: { x: number; y: number }) => ({
  "--x-l": `${l.x}%`, "--y-l": `${l.y}%`,
  "--x-p": `${p.x}%`, "--y-p": `${p.y}%`,
} as React.CSSProperties);

/** A seat's placement plus the direction its cards lie in, for both orientations. */
const atSeat = (
  l: { x: number; y: number }, p: { x: number; y: number }, plateAspect: number,
): React.CSSProperties => {
  const a = inward(l, TOPDOWN_RING_LANDSCAPE.cx, TOPDOWN_RING_LANDSCAPE.cy, plateAspect);
  const b = inward(p, TOPDOWN_RING_PORTRAIT.cx, TOPDOWN_RING_PORTRAIT.cy, 1 / plateAspect);
  /*
   * The magnitudes go out as absolute values alongside the signed ones.
   *
   * The push has to be long enough that the two-card block clears the portrait's
   * circle in whichever direction it leaves, and how long that is depends on the
   * direction: sideways it needs the block's half-WIDTH, downward its half-HEIGHT.
   * `topdown.css` mixes the two by |ux| and |uy|, which is exact on the axes and
   * generous in between; it cannot do that without the absolute values, and CSS `abs()`
   * is too new to rely on.
   */
  /*
   * Up and down go out as SIGNS, not as magnitudes, and that is the second audit's fix.
   *
   * `|uy|` cannot tell a seat on the top rail from one on the bottom, and the two are
   * not symmetric: above the portrait there is nothing, below it there is the name
   * plate. Pushing a downward seat's cards by the circle's radius buried them under
   * that plate on every top seat in every frame — 370 of 480 combinations. What CSS
   * needs in order to pick the right half-height is a 0/1 indicator and not a weight:
   * `max(-uy, 0)` on a diagonal seat is 0.43, and 0.43 of a name plate's height is not
   * a smaller plate, it is a wrong one.
   */
  return {
    ...at(l, p),
    "--ux-l": a.ux, "--uy-l": a.uy, "--ax-l": Math.abs(a.ux), "--ay-l": Math.abs(a.uy),
    "--sgn-dn-l": a.uy > 0 ? 1 : 0, "--sgn-up-l": a.uy > 0 ? 0 : 1,
    "--ux-p": b.ux, "--uy-p": b.uy, "--ax-p": Math.abs(b.ux), "--ay-p": Math.abs(b.uy),
    "--sgn-dn-p": b.uy > 0 ? 1 : 0, "--sgn-up-p": b.uy > 0 ? 0 : 1,
  } as React.CSSProperties;
};

/*
 * `atBet` — the inner bet ring — was removed in Phase 5 box 0(b) along with its one
 * caller. The hero was the last thing placed on it; every seat's chips have ridden on
 * the seat's own bearing since Phase 3b, and the hero's do now too. `SeatBox.betX/betY`
 * are still computed in `table-game.ts`, because the classic felt uses them.
 */

/**
 * Which way this seat's cards lie: toward the middle of the table.
 *
 * A player's cards are on the felt in FRONT of them, and from directly above "in front"
 * means toward the pot. So the direction is the unit vector from the seat to the
 * centre — down for a seat on the top rail, up for one at the bottom, inward for the
 * ones on the ends.
 *
 * Two earlier versions were worse. Always-above put the top seat's cards under the top
 * bar at heads-up. Above-or-below by which half the seat is in put a seat sitting just
 * above the centre line — a hair into the "upper" half — face down onto the head of the
 * seat below it, which is exactly what the harness caught at nine-handed on a landscape
 * phone.
 *
 * The percentages are scaled by the box's aspect before being normalised, because a
 * percentage of the width and a percentage of the height are different distances and an
 * unscaled vector points somewhere that is not the middle.
 */
function inward(seat: { x: number; y: number }, cx: number, cy: number, boxAspect: number) {
  const dx = (cx - seat.x) * boxAspect;
  const dy = cy - seat.y;
  const len = Math.hypot(dx, dy) || 1;
  return { ux: dx / len, uy: dy / len };
}

export function TopDownTable({
  state, lastAction, plate = DEFAULT_PLATE, plateAspect = DEFAULT_PLATE_ASPECT,
  context, wallet, chrome = "own", timers, expressions, taunts, shown, faces, actions, dealt, flights, motion, settings,
  onSettings, avatarUrl = null,
  deck = "none", payoff = null, potFlying = false, potFrozen = false,
}: TopDownTableProps) {
  /*
    Above the early return, not below it. A hook after a conditional return runs a
    different number of times depending on whether a hand is in progress, which is
    exactly the rules-of-hooks violation React cannot recover from. The same note is on
    `GameTable`, for the same reason.
  */
  const heroIndex = state ? state.seats.findIndex((s) => s.isSelf) : -1;
  /* Phase 3b: the pile under the pill, which lags the state while bets are in the air. */
  const collected = useCollected(state, potFrozen);
  /* The bubbles, and which one is newest — see `useBubbles` in `parts.tsx`. */
  const { bubbles, newest } = useBubbles(state, taunts, heroIndex);

  if (!state) {
    return (
      <div className="td-frame" style={{ "--plate-asp": plateAspect } as React.CSSProperties}>
        <div className="td-app td-app-empty"><p className="muted">Deal a hand to begin.</p></div>
      </div>
    );
  }

  const n = state.seats.length;
  const hero = heroIndex >= 0 ? state.seats[heroIndex]! : null;
  const land = seatPositions(n, heroIndex, TOPDOWN_RING_LANDSCAPE);
  const port = seatPositions(n, heroIndex, TOPDOWN_RING_PORTRAIT);

  const btnSeat = state.button;
  const btnLand = buttonPoint(land[btnSeat] ?? land[0]!, TOPDOWN_RING_LANDSCAPE.cx,
                              TOPDOWN_RING_LANDSCAPE.cy, BUTTON_OFFSET.landscape);
  const btnPort = buttonPoint(port[btnSeat] ?? port[0]!, TOPDOWN_RING_PORTRAIT.cx,
                              TOPDOWN_RING_PORTRAIT.cy, BUTTON_OFFSET.portrait);

  return (
    /*
      `.td-frame` is the container query context and `.td-app` is the layout. They are
      two elements because a container query unit measured ON the container resolves
      against the container's own ancestor rather than against itself — so the sizing
      tokens have to be declared one level inside. `topdown.css` has the full note.
    */
    <div className="td-frame" style={{ "--plate-asp": plateAspect } as React.CSSProperties}>
    <div className="td-app" style={motionVars(motion)}>
      {/* ------------------------------------------------------------- top bar --- */}
      {chrome === "own" && (
        <TopBar state={state} context={context} lastAction={lastAction} wallet={wallet}
                settings={settings} onSettings={onSettings} />
      )}

      {/* --------------------------------------------------------------- stage --- */}
      <div className="td-stage">
        <div className="td-feltfit">
          <div className="td-felt">
            <img className="td-plate" src={plate} alt="" aria-hidden="true" />

            {/*
              * ============ PHASE 5, BOX 0(b): THE HERO SITS AT THE TABLE ==========
              * Every seat is drawn here now, the player's included. `TOPDOWN-LAYOUT-
              * SPEC.md` §1 said "the hero's own cards are never drawn on the felt" and
              * put the player in a plate in the bottom-left corner; Tim played a
              * session on preview and the first thing he said was that he was not at
              * the table. He was right, and the spec was wrong: at a real table and in
              * every other client the player has a seat like everybody else, and a
              * table where eight people sit on the felt and the ninth floats in the
              * corner is a table the ninth person is watching rather than playing at.
              *
              * So the hero takes the seat `seatPositions()` has always reserved for
              * them at six o'clock (`heroGap` is what holds it open), with the same
              * portrait, the same plate, the same timer ring, the same ALL IN tag and
              * the same bet spot as any bot. Their two BIG cards stay big and move to
              * bottom-centre, directly under the seat, in `.td-herozone` below — the
              * one thing about the hero that is different, and it is different because
              * these are the only two cards on the table the player has to read.
              *
              * The only per-seat difference in this block is the portrait source (the
              * hero's face is a client fact — see `avatarUrl`) and the face-down peek,
              * which the hero does not get because the big cards ARE their hand.
              * ====================================================================
              */}
            {state.seats.map((s, i) => {
              const isHero = s.isSelf;
              /* A busted opponent's chair is cleared; the player's own never is —
                 they are still sitting there, and a rebuy is offered to them. */
              if (seatIsEmpty(s) && !isHero) return null;
              return (
                <div
                  key={`seat${i}`}
                  data-td="seat" data-seat={i}
                  className={seatClass(s, i, state, payoff, shown)}
                  style={atSeat(land[i]!, port[i]!, plateAspect)}
                >
                  {/* The bubble is NOT drawn here any more: it lives in the layer
                      above the felt, where it can be measured against the cards it
                      has to clear. See `BubbleLayer` in `flight.tsx`. The cards are
                      `SeatPeek` — the face-down peek, a showdown, or a Show. */}
                  <SeatPeek s={s} i={i} shown={shown} dealt={dealt} />

                  <span className="td-av" data-td="av">
                    <Portrait name={s.identity.name} expression={expressions?.[i]}
                              override={isHero ? avatarUrl : undefined}
                              slug={isHero ? undefined : faces?.[i]}
                              className="td-av-img" />
                    <TimerRing timer={timers?.[i]} />
                  </span>

                  <SeatTag s={s} motion={motion} />

                  {/*
                    * The bet rides on the SEAT, not on a ring of its own — see `SeatBet`.
                    * It used to sit at `betPull` along the same bearing, a lane that
                    * crossed the cards' on a phone in 250 of 480 sweeps; parented to the
                    * seat it cannot overlap the cards or the seat by construction.
                    */}
                  <SeatBet s={s} i={i} left={land[i]!.x > 50} flights={!!flights} />
                </div>
              );
            })}

            <DealerButton state={state} motion={motion} style={at(btnLand, btnPort)} />

            <DeckSpot deck={deck} />

            <Board board={state.board} />

            <PotWrap pot={state.pot} collected={collected} potFlying={potFlying} motion={motion} />
          </div>
        </div>
      </div>

      {/* ----------------------------------------------------------- hero zone --- */}
      {/*
        * What is left of the hero zone: the two cards, bottom-centre, directly below
        * the player's own chair, overlapping the near rail. Its own element rather than
        * a child of the seat because it must not be sized by the seat: these two cards
        * are the only ones on the table drawn at reading size.
        */}
      {hero && (
        <div className="td-herozone">
          <HeroHand hero={hero} heroIndex={heroIndex} dealt={dealt} payoff={payoff} />
        </div>
      )}

      {/* --------------------------------------------------------- action band --- */}
      <div className="td-actions">{actions}</div>

      <BoardStatus state={state} />

      {/* Phase 3: chips and cards in motion, above everything, measuring the felt. */}
      {flights && motion && <FlightLayer bus={flights} durations={motion} />}

      {/*
        * Phase 6: the bubbles, in a layer of their own above the flights — its own
        * element because the harness has no `FlightBus` and the 480-combination sweep
        * has to measure the REAL placer. `motion` is optional for the same reason.
        */}
      {bubbles.length > 0 && <BubbleLayer bubbles={bubbles} durations={motion ?? NORMAL} persist={!motion} />}

      <TalkStatus state={state} newest={newest} />
    </div>
    </div>
  );
}
